import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { usePermission } from "../../../features/auth/use-permission";
import { HistoryTabs } from "../../../features/change-history/components/history-tabs";
import { RecordHistory } from "../../../features/change-history/components/record-history";
import { historyTabSearchSchema } from "../../../features/change-history/history-search";
import { facilityPickQueryOptions } from "../../../features/facility-pick/api/facilities";
import { FacilityChooser } from "../../../features/facility-pick/facility-chooser";
import { FacilityZoneForm } from "../../../features/facility-profile/components/facility-zone-form";

/**
 * `RPM-028`'s facility-profile administration form (`J-276`).
 *
 * THE ROUTE COMPOSES TWO FEATURES, as `.claude/rules/coding.md` asks: the chooser is
 * `facility-pick`'s and the zone form is `facility-profile`'s, and the building is the one fact they
 * share. The chooser lists the buildings this account reaches with a current profile, which is
 * exactly the set whose zone can be changed; the server decides again on every request.
 *
 * AFTER A CHANGE THE PICK LIST'S COPY OF THE ZONE IS UPDATED IN PLACE, because it carries each
 * building's zone and the entry screen names it beside the time-taken field. The change's response
 * already says what the zone is, so nothing is read again.
 *
 * THE HISTORY TAB (`RPM-063`, `J-291`) lists the building's zone versions, each change one entry,
 * and is rendered only for an account holding `audit: ["read"]`.
 */
function FacilityZonesScreen() {
  const [facilityId, setFacilityId] = useState("");
  const client = useQueryClient();
  const { tab = "details" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const canReadHistory = usePermission({ audit: ["read"] });
  const form = (
    <FacilityZoneForm
      key={facilityId}
      facilityId={facilityId}
      onChanged={(ianaTimeZone) => {
        client.setQueryData(facilityPickQueryOptions().queryKey, (list) =>
          list === undefined
            ? list
            : {
                items: list.items.map((b) =>
                  b.facilityId === facilityId ? { ...b, ianaTimeZone } : b,
                ),
              },
        );
      }}
    />
  );
  return (
    <div className="flex flex-col gap-4 pt-4">
      <FacilityChooser value={facilityId} onChange={setFacilityId} />
      {facilityId === "" ? (
        form
      ) : (
        <HistoryTabs
          showHistory={canReadHistory}
          tab={tab}
          onTabChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, tab: next }), replace: true })
          }
          detailsLabel="Operative zone"
          details={form}
          history={
            <RecordHistory
              key={facilityId}
              entity="facility-profiles"
              records={[{ id: facilityId, label: "" }]}
              noun="Building"
              caption={(label) => `Change history of the time zone of ${label || "this building"}`}
              columnLabels={{ iana_time_zone: "Time zone" }}
            />
          }
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/admin/facility-zones")({
  validateSearch: historyTabSearchSchema,
  component: FacilityZonesScreen,
  staticData: { screenName: "Facilities" },
});
